import L10nService from '@ember-gettext/ember-l10n/services/l10n';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import {
  getUnitTypeForNumericParameterType,
  NumericRoastBatchParameterType,
} from 'fabscale-app/models/enums/roast-batch-parameter-type';
import { getDefaultUnit } from 'fabscale-app/models/enums/unit-systems';
import { Unit, UnitsRoastColor } from 'fabscale-app/models/enums/units';
import { EditingNumericParameter } from 'fabscale-app/models/editing-roast-batch-parameter';
import {
  NumericRoastBatchParameter,
  RoastBatch,
} from 'fabscale-app/models/roast-batch';
import ErrorParserService from 'fabscale-app/services/error-parser';
import SettingsService from 'fabscale-app/services/settings';
import StoreRoastBatchService from 'fabscale-app/services/store/roast-batch';
import { logError } from 'fabscale-app/utilities/utils/log-error';

interface Args {
  roastBatch: RoastBatch;
  selectedNumericPropertyTypes: NumericRoastBatchParameterType[];
  closeEditMode: () => void;
  onEditRowSuccess: (roastBatch: RoastBatch) => void;
}

export default class RoastBatchesOverviewTableEditParameters extends Component<Args> {
  @service settings: SettingsService;
  @service('store/roast-batch') roastBatchStore: StoreRoastBatchService;
  @service('error-parser') errorParser: ErrorParserService;
  @service l10n: L10nService;

  @tracked error?: string;

  get unitSystem() {
    return this.settings.locationSettings.unitSystem;
  }

  @cached
  get numericRoastBatchParameters() {
    let { unitSystem } = this;
    let { numericRoastBatchParameters } = this.args.roastBatch;

    return this.args.selectedNumericPropertyTypes.map((parameterType) => {
      let param = numericRoastBatchParameters.find(
        (param) => param.parameterType === parameterType
      );

      let unitType = getUnitTypeForNumericParameterType(parameterType);
      let defaultUnit =
        unitType === UnitsRoastColor
          ? this.settings.locationSettings.defaultRoastColorUnit
          : getDefaultUnit(unitSystem, unitType);

      return new EditingNumericParameter({
        parameterType,
        value: param?.value,
        unit: param?.unit || defaultUnit,
        units: unitType,
        externalId: param?.externalId,
      });
    });
  }

  get firstEmtpyParameter() {
    return this.numericRoastBatchParameters.find(
      (param) => typeof param.value === 'undefined'
    );
  }

  @action
  updateNumericRoastBatchParameterValue(
    param: EditingNumericParameter,
    value: number | undefined
  ) {
    param.value = value;
  }

  @action
  updateNumericRoastBatchParameterUnit(
    param: EditingNumericParameter,
    unit: Unit
  ) {
    param.unit = unit;
  }

  submitFormTask = dropTask(async () => {
    let roastBatchId = this.args.roastBatch.id;

    let changedParameters = this.numericRoastBatchParameters.filter(
      (param) => param.hasChanged
    );

    let numericRoastBatchParameters: NumericRoastBatchParameter[] =
      changedParameters.map((param) => {
        let { value, unit, externalId, parameterType } = param;

        return { value: value as number, unit, externalId, parameterType };
      });

    try {
      await this.roastBatchStore.setRoastBatchParameters(roastBatchId, {
        numericRoastBatchParameters,
      });
    } catch (error) {
      logError(error);

      this.error = this.errorParser.getErrorMessage(error);
      return;
    }

    try {
      let updatedRoastBatch: RoastBatch = await this.roastBatchStore.findById(
        roastBatchId
      );

      this.args.onEditRowSuccess(updatedRoastBatch);
    } catch (error) {
      logError(error);

      this.error = this.l10n.t(
        'An error occurred while trying to reload the roast.'
      );
    }
  });
}
